import React from 'react';
import { connect } from 'react-redux';
import { buildSystemFields, buildSystemCopyText } from '../util/systemDetails';
import { getProductMode, PRODUCT_MODE } from '../backend/productMode';
import { getBackend } from '../backend';
import Sheet from '../common/Sheet';
import { matchTabletSheet, onTabletSheetChange } from '../util/tabletSheet';

/**
 * SystemInfoPopover — the bottom-right (i) system-details popover (redesign #86bb5dx62,
 * Mock D4). The home for the identity + status info pulled out of the header/status bar:
 *   Controller (eView) · System · Serial · Type · Version · Online · Storage · Days recording · Viewer.
 * CLICK the (i) to pin it open (hover was finicky to interact with — David); Serial + Version
 * are copy-to-clipboard, and "Copy all details" copies the whole block — plus the logged-in
 * user (copy-only, not a visible row — David) — so a customer can paste it to support and we know
 * whose session it is. The bottom bar keeps its own always-visible "N Days Rec" (unchanged).
 * Rendered in the desktop bottom bar → desktop-only via CSS.
 */
class SystemInfoPopover extends React.Component {
    state = { pinned: false, copied: null, tabletSheet: matchTabletSheet() };

    componentDidMount() {
        document.addEventListener('mousedown', this.onDocMouseDown);
        this._offTablet = onTabletSheetChange((m) => this.setState({ tabletSheet: m }));
    }
    componentWillUnmount() {
        document.removeEventListener('mousedown', this.onDocMouseDown);
        if (this._copyTimer) clearTimeout(this._copyTimer);
        if (this._offTablet) this._offTablet();
    }
    onDocMouseDown = (e) => {
        if (this.state.pinned && this._wrap && !this._wrap.contains(e.target)) this.setState({ pinned: false });
    };
    togglePin = () => this.setState((s) => ({ pinned: !s.pinned }));

    copy = (field, text) => {
        try { if (navigator.clipboard) navigator.clipboard.writeText(String(text)); } catch (e) { /* clipboard blocked — ignore */ }
        this.setState({ copied: field });
        if (this._copyTimer) clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => this.setState({ copied: null }), 900);
    };

    // Field list + copy-all text come from the shared util (util/systemDetails) so this popover and
    // the Setup > System Info page never drift. The logged-in user is appended to Copy-all only
    // (not a rendered row — David: so support sees whose session reported the issue).
    fields = () => buildSystemFields(this.props);
    copyAllText = () => buildSystemCopyText(this.fields());

    // Copy the whole block — a customer can paste it to support (David).
    copyAll = () => this.copy('all', this.copyAllText());

    render() {
        const { pinned, copied, tabletSheet } = this.state;
        const fields = this.fields();
        const copyIcon = (
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" /></svg>
        );

        const body = (
            <>
                <div className="live-sysinfo__hd">
                    <span>System details</span>
                    <button className={'live-sysinfo__copyall' + (copied === 'all' ? ' live-sysinfo__copyall--copied' : '')} onClick={this.copyAll} title="Copy all details to clipboard">
                        {copyIcon}<span>{copied === 'all' ? 'Copied' : 'Copy all'}</span>
                    </button>
                </div>
                {/* No per-field copy icons — "Copy all" is the single copy action (David). */}
                {fields.filter((f) => !f.copyOnly).map((f) => (
                    <div key={f.k} className="live-sysinfo__row">
                        <span className="live-sysinfo__l">{f.k}</span>
                        <span className={'live-sysinfo__v' + (f.muted ? ' live-sysinfo__v--muted' : '')} title={f.v}>{f.v}</span>
                    </div>
                ))}
            </>
        );

        return (
            <div className={'live-sysinfo' + (pinned ? ' live-sysinfo--show' : '')} ref={(el) => { this._wrap = el; }}>
                <button className="live-sysinfo__btn" onClick={this.togglePin} title="System details" aria-haspopup="dialog" aria-expanded={pinned}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" /></svg>
                </button>
                {/* Tablet (< 1280): the mobile bottom SHEET; desktop (>= 1280): the anchored popover. The custom
                    header (with Copy-all) rides inside the sheet body, so no separate Sheet title. */}
                {tabletSheet ? (
                    pinned && (
                        <Sheet className="app-sheet live-bar-sheet" ariaLabel="System details" onClose={() => this.setState({ pinned: false })}>
                            {body}
                        </Sheet>
                    )
                ) : (
                    <div className="live-sysinfo__pop" role="dialog" aria-label="System details">
                        {body}
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const { sName, fEview, bSerial, activeServerSerial, sVersion, sReleaseVersion, fReleaseCandidate, authServers, usersOnline, machineStatus } = state.server;
    // Cloud (#86bb5dx62): gate the local-only rows + show the cloudapi version. isCloud flips the
    // buildSystemFields branch; cloudApiVersion reads straight off the persisted cloud session.
    const isCloud = getProductMode(state) === PRODUCT_MODE.CLOUD;
    return {
        sName, fEview, bSerial, activeServerSerial, sVersion, sReleaseVersion, fReleaseCandidate, authServers,
        usersOnline, machineStatus,   // Online + storage detail (moved here from the bottom bar)
        controllerSerial: bSerial,    // the login/controller NVR serial (eView)
        userName: (state.user && state.user.sName) || '',   // logged-in user — copy-only (David)
        isCloud,
        cloudApiVersion: isCloud ? (state.utility.cloudApiVersion || getBackend().getBackendVersion()) : null
    };
};

export { SystemInfoPopover };
export default connect(mapStateToProps)(SystemInfoPopover);
